import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { Heart, MessageSquare, ArrowLeft, Building2, ChevronRight, X } from 'lucide-react';

function LabPortfolioGallery({ user }) {
    const { labId } = useParams();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [labInfo, setLabInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [likedPosts, setLikedPosts] = useState(new Set());
    const [selectedPost, setSelectedPost] = useState(null);
    const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);

    const loadData = useCallback(async () => {
        if (!labId) return;
        setLoading(true);
        try {
            // 기공소 정보
            const adDoc = await getDoc(doc(db, 'labAdvertisements', labId));
            const userDoc = await getDoc(doc(db, 'users', labId));
            const ad = adDoc.exists() ? adDoc.data() : {};
            const usr = userDoc.exists() ? userDoc.data() : {};
            setLabInfo({
                name: ad.businessName || usr.companyName || usr.businessName || usr.displayName || '기공소',
                profileImage: ad.profileImageUrl || ad.profileImage || '',
                region: (ad.address || usr.address || '').split(' ').slice(0, 2).join(' '),
                slogan: ad.slogan || '',
                specialties: ad.specialties || [],
            });

            // 포트폴리오 게시물
            const q = query(collection(db, 'labPosts'), where('labId', '==', labId), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));

            // 좋아요
            if (auth.currentUser) {
                const likesQ = query(collection(db, 'labPostLikes'), where('userId', '==', auth.currentUser.uid));
                const likesSnap = await getDocs(likesQ);
                setLikedPosts(new Set(likesSnap.docs.map(d => d.data().postId)));
            }
        } catch (e) {
            console.error('포트폴리오 로드 실패:', e);
        }
        setLoading(false);
    }, [labId]);

    useEffect(() => { loadData(); }, [loadData]);

    const toggleLike = async (post, e) => {
        e?.stopPropagation();
        if (!auth.currentUser) { navigate('/login'); return; }
        const uid = auth.currentUser.uid;
        const likeId = `${uid}_${post.id}`;
        try {
            const { updateDoc } = await import('firebase/firestore');
            if (likedPosts.has(post.id)) {
                await deleteDoc(doc(db, 'labPostLikes', likeId));
                setLikedPosts(prev => { const s = new Set(prev); s.delete(post.id); return s; });
                setPosts(prev => prev.map(p => p.id === post.id ? { ...p, likes: Math.max(0, (p.likes || 1) - 1) } : p));
                await updateDoc(doc(db, 'labPosts', post.id), { likes: Math.max(0, (post.likes || 1) - 1) });
            } else {
                await setDoc(doc(db, 'labPostLikes', likeId), { userId: uid, postId: post.id, createdAt: serverTimestamp() });
                setLikedPosts(prev => new Set([...prev, post.id]));
                setPosts(prev => prev.map(p => p.id === post.id ? { ...p, likes: (p.likes || 0) + 1 } : p));
                await updateDoc(doc(db, 'labPosts', post.id), { likes: (post.likes || 0) + 1 });
            }
        } catch (e) { console.error('좋아요 실패:', e); }
    };

    if (loading) {
        return <div style={S.loadingWrap}><div style={S.spinner} /><p style={{ color: '#94a3b8' }}>로딩 중...</p></div>;
    }

    return (
        <div style={S.container}>
            {/* 뒤로가기 */}
            <button onClick={() => navigate(-1)} style={S.backBtn}>
                <ArrowLeft size={18} /> 뒤로
            </button>

            {/* 기공소 헤더 */}
            {labInfo && (
                <div style={S.labHeader}>
                    <div style={S.labHeaderLeft}>
                        {labInfo.profileImage ? (
                            <img src={labInfo.profileImage} alt="" style={S.labAvatar} />
                        ) : (
                            <div style={S.labAvatarPlaceholder}>{labInfo.name?.[0]}</div>
                        )}
                        <div>
                            <h1 style={S.labName}>{labInfo.name}</h1>
                            {labInfo.region && <p style={S.labRegion}>{labInfo.region}</p>}
                            {labInfo.slogan && <p style={S.labSlogan}>{labInfo.slogan}</p>}
                        </div>
                    </div>
                    <div style={S.labHeaderRight}>
                        <div style={S.labStat}>
                            <span style={S.labStatNum}>{posts.length}</span>
                            <span style={S.labStatLabel}>게시물</span>
                        </div>
                        <div style={S.labStat}>
                            <span style={S.labStatNum}>{posts.reduce((s, p) => s + (p.likes || 0), 0)}</span>
                            <span style={S.labStatLabel}>좋아요</span>
                        </div>
                    </div>
                </div>
            )}

            {/* 기공소 프로필 보기 버튼 */}
            <button onClick={() => navigate(`/lab/${labId}`)} style={S.profileBtn}>
                <Building2 size={16} /> 기공소 프로필 보기 <ChevronRight size={16} />
            </button>

            {/* 전문 분야 태그 */}
            {labInfo?.specialties?.length > 0 && (
                <div style={S.tagRow}>
                    {labInfo.specialties.map((s, i) => (
                        <span key={i} style={S.tag}>{s}</span>
                    ))}
                </div>
            )}

            {/* 포트폴리오 그리드 */}
            {posts.length === 0 ? (
                <div style={S.emptyState}>
                    <p style={{ fontSize: 40, marginBottom: 12 }}>&#128247;</p>
                    <p style={{ color: '#94a3b8', fontSize: 14 }}>아직 게시물이 없습니다</p>
                </div>
            ) : (
                <div style={S.grid}>
                    {posts.map(post => (
                        <div key={post.id} style={S.gridItem} onClick={() => { setSelectedPost(post); setCurrentPhotoIdx(0); }}>
                            <img src={post.photos?.[0]} alt="" style={S.gridImg} />
                            <div style={S.gridOverlay}>
                                <span style={S.gridStat}><Heart size={14} fill="#fff" color="#fff" /> {post.likes || 0}</span>
                                <span style={S.gridStat}><MessageSquare size={14} color="#fff" /> {post.commentCount || 0}</span>
                            </div>
                            {post.photos?.length > 1 && (
                                <div style={S.gridMulti}>+{post.photos.length - 1}</div>
                            )}
                            <div style={S.gridCategory}>{post.category}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* 사진 상세 모달 */}
            {selectedPost && (
                <div style={S.modalOverlay} onClick={() => setSelectedPost(null)}>
                    <div style={S.modal} onClick={e => e.stopPropagation()}>
                        <button onClick={() => setSelectedPost(null)} style={S.modalClose}><X size={20} /></button>
                        <div style={S.modalBody}>
                            <div style={S.modalImgWrap}>
                                <img src={selectedPost.photos?.[currentPhotoIdx]} alt="" style={S.modalImg} />
                                {selectedPost.photos?.length > 1 && (
                                    <div style={S.modalDots}>
                                        {selectedPost.photos.map((_, i) => (
                                            <div key={i} onClick={() => setCurrentPhotoIdx(i)}
                                                style={{ width: 8, height: 8, borderRadius: '50%', background: i === currentPhotoIdx ? '#6366f1' : '#d1d5db', cursor: 'pointer' }} />
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div style={S.modalInfo}>
                                <div style={S.modalCat}>{selectedPost.category}</div>
                                {selectedPost.description && <p style={S.modalDesc}>{selectedPost.description}</p>}
                                {selectedPost.tags?.length > 0 && (
                                    <div style={S.modalTags}>
                                        {selectedPost.tags.map((t, i) => <span key={i} style={S.modalTag}>#{t}</span>)}
                                    </div>
                                )}
                                <div style={S.modalActions}>
                                    <button onClick={(e) => toggleLike(selectedPost, e)} style={S.modalLikeBtn}>
                                        <Heart size={18} fill={likedPosts.has(selectedPost.id) ? '#ef4444' : 'none'} color={likedPosts.has(selectedPost.id) ? '#ef4444' : '#64748b'} />
                                        <span>{selectedPost.likes || 0}</span>
                                    </button>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, color: '#64748b' }}>
                                        <MessageSquare size={16} color="#6366f1" /> {selectedPost.commentCount || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    container: { maxWidth: 800, margin: '0 auto', padding: '24px 16px 60px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 },
    spinner: { width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

    backBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 20, padding: 0 },

    labHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 16 },
    labHeaderLeft: { display: 'flex', alignItems: 'center', gap: 14 },
    labAvatar: { width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' },
    labAvatarPlaceholder: { width: 60, height: 60, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 800 },
    labName: { fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 },
    labRegion: { fontSize: 13, color: '#6366f1', marginTop: 2 },
    labSlogan: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
    labHeaderRight: { display: 'flex', gap: 20 },
    labStat: { textAlign: 'center' },
    labStatNum: { display: 'block', fontSize: 20, fontWeight: 800, color: '#0f172a' },
    labStatLabel: { fontSize: 11, color: '#94a3b8' },

    profileBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, fontSize: 14, fontWeight: 700, color: '#6366f1', cursor: 'pointer', marginBottom: 16 },

    tagRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 },
    tag: { padding: '4px 12px', background: '#f0f0ff', color: '#6366f1', borderRadius: 14, fontSize: 11, fontWeight: 600 },

    emptyState: { textAlign: 'center', padding: '60px 20px' },

    grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 },
    gridItem: { position: 'relative', aspectRatio: '1', overflow: 'hidden', cursor: 'pointer', borderRadius: 2 },
    gridImg: { width: '100%', height: '100%', objectFit: 'cover' },
    gridOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, opacity: 0, transition: 'opacity 0.2s' },
    gridStat: { color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 },
    gridMulti: { position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 700 },
    gridCategory: { position: 'absolute', bottom: 6, left: 6, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600 },

    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 },
    modal: { background: '#fff', borderRadius: 16, maxWidth: 600, width: '100%', maxHeight: '90vh', overflow: 'hidden', position: 'relative' },
    modalClose: { position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 },
    modalBody: {},
    modalImgWrap: { position: 'relative', background: '#000' },
    modalImg: { width: '100%', maxHeight: '60vh', objectFit: 'contain', display: 'block' },
    modalDots: { position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 },
    modalInfo: { padding: 20 },
    modalCat: { display: 'inline-block', padding: '3px 12px', background: '#f0f0ff', color: '#6366f1', borderRadius: 12, fontSize: 12, fontWeight: 600, marginBottom: 10 },
    modalDesc: { fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 10 },
    modalTags: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 },
    modalTag: { fontSize: 12, color: '#6366f1', fontWeight: 500 },
    modalActions: { display: 'flex', alignItems: 'center', gap: 16, paddingTop: 12, borderTop: '1px solid #f1f5f9' },
    modalLikeBtn: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151', padding: '6px 0' },
};

// hover 효과를 위한 CSS 추가
const styleSheet = document.createElement('style');
styleSheet.textContent = `
    .portfolio-grid-item:hover .portfolio-grid-overlay { opacity: 1 !important; }
`;
if (!document.querySelector('#portfolio-gallery-styles')) {
    styleSheet.id = 'portfolio-gallery-styles';
    document.head.appendChild(styleSheet);
}

export default LabPortfolioGallery;
